import React, { useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import styled from "styled-components/macro";
import { FormattedMessage, injectIntl } from "react-intl";
import ReactGA from "react-ga4";
import { latLng, featureGroup, marker } from "leaflet";
import { xor } from "lodash";

import { DEFAULT_VIEWPORT } from "../../store/reducers/search";
import { getSearchParams } from "../../utils/params";
import { distanceGeo, hasPosition } from "../../utils/calc";

import { SUGGESTIONS } from "./suggestions";
import Lead from "./Lead";
import LocationPrompt from "./LocationPrompt";
import ResultsPanel from "./ResultsPanel";
import SearchBar from "../Search/SearchBar";
import QuickView from "./QuickView";
import SearchViewToggle from "../../components/SearchViewToggle";
import { Icon, Heading3 } from "../../components";
import UserNav from "../../components/UserNav";
import Map, { flatBounds } from "../../components/Map";
import mapBlur from "../../assets/map-blur.jpg";

/* Re-exported for the search pages that import it from here. */
export { SUGGESTIONS };

const DEFAULT_ZOOM = 13;
const MIN_ZOOM = 9;
const MAX_ZOOM = 16;
const MAX_LOCATION_ZOOM = 14;
const MAX_PRICE = 500;

/* Waits for the map to come to rest before searching, so a drag across the country fires one
   request instead of one per step. */
const SEARCH_DEBOUNCE = 600;

/* The search covers half again the visible extent, so that a short pan inside what was
   already loaded costs no request. */
const FETCH_MARGIN = 1.5;

/* The list keeps to what the map roughly shows, a little past the corners so a small pan does
   not drop the results that just slid out of frame. */
const VISIBLE_MARGIN = 1.25;

/* A pan shorter than this share of the fetched radius still sits inside what was loaded. It
   has to stay below 1 - 1 / FETCH_MARGIN, or the view can reach past the loaded edge before
   the next search is due. */
const MOVE_RATIO = 0.3;

const MIN_RADIUS = 1;
const MAX_RADIUS = 250;

/* Offerings held at no location have no marker, so the map starts without them. The map keeps
   this choice to itself, and the list search keeps its own. */
const MAP_LOCATION_TYPES = ["on_site"];

export const isSameLocation = (l0, l1) => l0 && l1 && l0.lat === l1.lat && l0.lng === l1.lng;
export const zoomRadius = zoom => {
  if (zoom > 15) {
    return 1;
  } else if (zoom > 14) {
    return 2;
  } else if (zoom > 13) {
    return 3;
  } else if (zoom > 12) {
    return 5;
  } else if (zoom > 11) {
    return 10;
  } else if (zoom > 10) {
    return 20;
  } else if (zoom > 8) {
    return 100;
  }
  return 250;
};

/* The map's current extent as a radius: half its diagonal, the distance from its centre to a
   corner. Falls back to the zoom table until leaflet has reported bounds. */
export const viewportRadius = viewport => {
  if (!viewport) {
    return null;
  }

  const { bounds, center, zoom } = viewport;

  if (!bounds || !center) {
    return zoomRadius(zoom);
  }

  return distanceGeo(center, bounds.from);
};

/* What a search asks the backend for. Fetching well past the edges is what keeps the map from
   searching again on every small move. */
export const searchRadius = viewport =>
  Math.min(MAX_RADIUS, Math.max(MIN_RADIUS, Math.round(viewportRadius(viewport) * FETCH_MARGIN)));

/* The locations the current view covers: everything within reach of the map's centre, rather
   than strictly inside its edges. Capping by distance instead of by bounds means a small pan
   no longer drops the results that just slid out of frame. Until leaflet has reported bounds
   nothing is filtered out. */
export const withinViewport = (locations, viewport) => {
  if (!viewport || !viewport.bounds || !viewport.center) {
    return locations || [];
  }

  const radius = viewportRadius(viewport) * VISIBLE_MARGIN;

  return (locations || []).filter(location => distanceGeo(viewport.center, location) <= radius);
};

export const isMobileView = window.innerWidth < 641;

/* The largest viewport, not the dynamic one: the map paints the whole window, including the
   strip the browser's own bar sits over, so sliding that bar in or out never uncovers a gap
   and never resizes Leaflet. Everything that floats over the map is fixed instead, which
   keeps it inside the part of the window the bars leave visible. */
const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  height: 100lvh;
  overflow: hidden;
  background-color: ${props => props.theme.gray200};
`;

const SearchWrapper = styled.div`
  position: absolute;
  inset: 0;
  display: block;
`;


const Content = styled.div`
  max-width: 100%;
`;

/* Grab affordance for the bottom sheet; the sheet itself is toggled by the list button. */
const SheetHandle = styled.span`
  display: block;
  width: 2.5rem;
  height: 0.25rem;
  margin: 0.5rem auto 0.75rem;
  border-radius: 0.125rem;
  background-color: ${props => props.theme.gray300};

  @media (${props => props.theme.desktopScreen}) {
    display: none;
  }
`;

const LiftedContent = styled.section`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 12;
  height: 88%;
  height: 88dvh;
  padding: 0.5rem 0 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  background-color: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 -0.5rem 2.5rem rgba(0, 0, 0, 0.25);
  border-radius: ${props =>
    `${props.theme.borderRadiusLarge} ${props.theme.borderRadiusLarge} 0 0`};
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);

  ${({ collapsed }) =>
    collapsed &&
    `
    transform: translateY(100%);
    pointer-events: none;
    visibility: hidden;
  `}

  @media (${props => props.theme.desktopScreen}) {
    left: auto;
    width: 24rem;
    height: auto;
    top: 10rem;
    right: 1rem;
    bottom: 1rem;
    border-radius: ${props => props.theme.borderRadiusMedium};

    ${({ collapsed }) =>
      collapsed &&
      `
      transform: translateX(calc(100% + 1.5rem));
    `}
  }
`;

const MapWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  isolation: isolate;
  background-image: url('${mapBlur}');
  background-size: cover;
  opacity: ${props => (props.showMap ? "1" : "0.4")};
  transition: opacity 1s;

  .leaflet-container {
    height: 100%;
  }
`;

const NoResults = styled.div`
  padding: 1rem;
  background-color: ${props => props.theme.white};
  box-shadow: ${props => props.theme.boxShadowLifted};
  border-radius: ${props => props.theme.borderRadius};
  margin: 1rem;
  max-width: 90%;
  text-align: center;
  margin: 0.5rem auto;
  display: block;

  ${Heading3} {
    font-size: ${props => props.theme.fontSizes.default};
    color: ${props => props.theme.info};
    margin-bottom: 0;
  }

  & > div {
    display: none;
  }

  @media (${props => props.theme.desktopScreen}) {
    & > div {
      display: block;
    }

    ${Heading3} {
      font-size: 1.75rem;
      margin-bottom: 0.5rem;
    }
  }
`;


const ResultsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.6rem 0.9rem;
  border: none;
  border-radius: 999px;
  background-color: ${props => (props.active ? props.theme.info : "rgba(255, 255, 255, 0.92)")};
  color: ${props => (props.active ? props.theme.white : props.theme.dark)};
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.25);
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
`;

const BottomBar = styled.div`
  position: fixed;
  left: 50%;
  bottom: calc(1rem + env(safe-area-inset-bottom));
  transform: translateX(-50%);
  z-index: 21;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (${props => props.theme.desktopScreen}) {
    left: 1rem;
    transform: none;
  }
`;


/* Leaves the map for the list search, which searches without a viewport. */


const TopBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 20;
`;

const Actions = styled.div`
  position: fixed;
  top: calc(3rem + env(safe-area-inset-top));
  left: 0;
  right: 0;
  z-index: 19;
  display: flex;
  justify-content: center;
  padding: 0.5rem;
  pointer-events: none;

  & > * {
    pointer-events: all;
    max-width: 100%;
  }

  @media (${props => props.theme.tabletScreen}) {
    top: 5rem;
  }
`;


const Search = ({
  address,
  center,
  term,
  changeAddress,
  clearAddress,
  searchMapResults,
  listView,
  isLoading,
  changeListView,
  results,
  onlineResults,
  metadata,
  filters,
  setFilters,
  setFilter,
  intl,
  clearSearch,
}) => {
  const params = getSearchParams() || {};

  // Everything on this page hangs off a position: no position, no map, no results
  const positioned = hasPosition(center);

  const [defaultViewport, setDefaultViewport] = useState(null);
  const [viewport, setViewport] = useState(null);
  const [stateTerm, setStateTerm] = useState(params.term || term || "");
  const [searched, setSearched] = useState(false);
  const [location, setLocation] = useState(null);
  const [bounds, setBounds] = useState(null);
  const [editFilters, setEditFilters] = useState(false);
  const [locationTypes, setLocationTypes] = useState(MAP_LOCATION_TYPES);

  const mapFilters = useMemo(() => ({ ...filters, locationTypes }), [filters, locationTypes]);

  // Save origin viewport for search optimization on map move
  const origin = useRef(null);

  // Term and filters the origin viewport was searched with
  const lastQuery = useRef(null);

  // The map opens on the map. The results sheet is something the visitor asks for, so it
  // starts closed however it was left last time.
  useEffect(() => {
    changeListView(false);
  }, [changeListView]);

  // Initial set of center after incoming search request by params
  useEffect(() => {
    const params = getSearchParams() || {};
    const { address, lat, lng } = params;

    if (lat && lng) {
      changeAddress(address, parseFloat(lat), parseFloat(lng));
    }
  }, [changeAddress, changeListView]);

  // Update viewport when center (redux) has changed
  useEffect(() => {
    if (!positioned) {
      setDefaultViewport(null);
      setViewport(null);
      setSearched(false);
      return;
    }

    const zoom =
      center.lat === DEFAULT_VIEWPORT.center.lat && center.lng === DEFAULT_VIEWPORT.center.lng
        ? DEFAULT_VIEWPORT.zoom
        : DEFAULT_ZOOM;

    setDefaultViewport(currentViewport => {
      const params = getSearchParams() || {};
      const lat = params.lat && parseFloat(params.lat);
      const lng = params.lng && parseFloat(params.lng);
      if (!currentViewport && lat && lng) {
        return { center: { lat, lng }, zoom };
      }
      return { ...currentViewport, center, zoom };
    });
  }, [center, positioned, setDefaultViewport, setSearched]);

  // Main search effect. Should be called on any change to viewport or search term
  useEffect(() => {
    // Do not search if location is needed but blank
    if (!viewport) {
      clearSearch();
      return;
    }

    const radius = searchRadius(viewport);
    const query = JSON.stringify([stateTerm, mapFilters]);

    // Skip only while the map still shows what was searched: same term and filters, same
    // zoom, and a pan that stayed well inside the loaded radius. Anything else re-fetches
    // for the area now in view.
    if (origin.current && lastQuery.current === query) {
      const moved = distanceGeo(origin.current.center, viewport.center);

      if (origin.current.zoom === viewport.zoom && moved < radius * MOVE_RATIO) {
        return;
      }
    }

    const timeout = setTimeout(() => {
      origin.current = viewport;
      lastQuery.current = query;

      searchMapResults(stateTerm, viewport.center, { ...mapFilters, radius });
    }, SEARCH_DEBOUNCE);

    return () => clearTimeout(timeout);
  }, [stateTerm, viewport, mapFilters, searchMapResults, clearSearch]);

  // Sets the viewport whenever a location changed (e.g. after Marker select)
  useEffect(() => {
    if (!location) {
      return;
    }

    setDefaultViewport(currentViewport => ({
      ...currentViewport,
      center: latLng(location.lat, location.lng),
      zoom: Math.min(MAX_LOCATION_ZOOM),
    }));
  }, [location, setDefaultViewport]);

  // NOTE: unfortunantely this gets triggered after router event, but still leave it
  // in case the component unmounts of other reasons
  useEffect(() => {
    // Nothing to prepare
    return () => {
      clearSearch();
    };
  }, [clearSearch]);

  // NOTE: only set bounds on initial map entry, but not on updated searches
  useEffect(() => {
    if (searched) {
      return;
    }

    if (!metadata || !metadata.bounds) {
      return;
    }

    if (!positioned || !origin.current) {
      return;
    }

    if (center.lat !== origin.current.lat || center.lng !== origin.current.lng) {
      return;
    }

    setBounds(metadata.bounds);
    setSearched(true);
  }, [metadata, center, positioned, searched]);

  // Trigger new search
  const onCategoryClick = ({ name }) => {
    if (
      name &&
      window.confirm(
        intl.formatMessage({ id: "pages.SearchMap.searchCategory" }, { category: name }),
      )
    ) {
      onSubmit(name);
      ReactGA.event({
        category: "Category",
        action: name || "",
      });
    }
  };

  const showAllResults = () => {
    if (!results || results.length < 1) {
      return;
    }

    const markers = featureGroup(results.map(l => marker([l.lat, l.lng])));
    const bounds = flatBounds(markers.getBounds());

    setBounds(bounds);
  };

  // Opens the quick view, which carries the links onward. A second tap on the same pin used
  // to open the provider in a new tab, which the quick view's buttons now do explicitly.
  const onLocationSelect = newLocation => {
    setLocation(newLocation);

    ReactGA.event({
      category: "SearchMarkerClick",
      action: ((newLocation.providers || [])[0] || {}).slug || "-",
    });
  };

  const updateFilters = (key, value) => {
    if (key === "locationTypes") {
      setLocationTypes(xor(locationTypes, value));
      return;
    }

    setFilters({ ...filters, [key]: xor(filters[key], value) });
  };

  // Resets the map's location types to its own default and leaves the list search's alone
  const onFiltersReset = defaults => {
    setLocationTypes(MAP_LOCATION_TYPES);
    setFilters({ ...defaults, locationTypes: filters.locationTypes });
  };

  const onMaxPriceChange = value => {
    setFilter({ maxPrice: value >= MAX_PRICE ? null : value });
  };

  const onRadiusChange = radius => {
    setFilter({ radius });
  };

  // Update search term and reset origin to trigger new search
  const onSubmit = value => {
    origin.current = null;
    setStateTerm(value);
  };

  // A new address was selected from auto complete field
  const onAddressSelect = (address, lat, lng) => {
    clearSearch();
    origin.current = null;
    changeAddress(address, lat, lng);
  };

  // Giving up the position takes the map with it, so the search has to go too
  const onAddressClear = () => {
    origin.current = null;
    lastQuery.current = null;
    clearAddress();
  };

  const showMap = defaultViewport && hasPosition(defaultViewport.center);

  // Several offerings can share one point, so the marker count understates what was found.
  // Counted over the same locations the list shows, so button and panel never disagree.
  const resultCount = withinViewport(results, viewport || defaultViewport).reduce(
    (total, location) => total + location.results.length,
    0,
  );

  return (
    <Wrapper>
      <SearchWrapper>
        <MapWrapper showMap={showMap} onClick={() => setLocation(null)}>
          {showMap && (
            <Map
              defaultViewport={defaultViewport}
              locations={results}
              currentLocation={location}
              onViewportChanged={setViewport}
              onMarkerClick={onLocationSelect}
              defaultBounds={bounds}
              options={{
                minZoom: MIN_ZOOM,
                maxZoom: MAX_ZOOM,
                zoomControl: false,
                boundsOptions: { padding: [-100, 0] },
              }}
            />
          )}
        </MapWrapper>

        <TopBar>
          <UserNav />
        </TopBar>

        <Actions>
          <SearchBar
            stateTerm={stateTerm}
            onSubmit={onSubmit}
            isLoading={isLoading}
            editFilters={editFilters}
            setEditFilters={setEditFilters}
            totalCount={resultCount}
            filters={mapFilters}
            setFilters={onFiltersReset}
            center={center}
            address={address}
            onAddressSelect={onAddressSelect}
            onAddressClear={onAddressClear}
            onMaxPriceChange={onMaxPriceChange}
            onRadiusChange={onRadiusChange}
            updateFilters={updateFilters}
            showLocation={false}
          />
        </Actions>

        {!listView && location && <QuickView location={location} onClose={() => setLocation(null)} />}

        {!positioned && (
          <LocationPrompt
            address={address}
            onSelect={(selected, { lat, lng }) => onAddressSelect(selected, lat, lng)}
            onClear={onAddressClear}
          />
        )}

        {/* Without a position there is nothing to list, not even the online offerings: they
            carry no coordinates, so they would be the only thing on an empty map. */}
        {positioned && (
          <LiftedContent collapsed={!listView}>
            <SheetHandle />
            <Content>
              {results.length > 0 || onlineResults.length > 0 ? (
                <ResultsPanel
                  active={listView}
                  locations={results || []}
                  onlineResults={onlineResults || []}
                  currentCenter={viewport || defaultViewport}
                  currentLocation={location}
                  onSelect={onLocationSelect}
                  onCategoryClick={onCategoryClick}
                  onZoomOut={showAllResults}
                  franchise={metadata && metadata.franchise}
                  term={stateTerm}
                />
              ) : (
                searched && (
                  <NoResults>
                    <Heading3>
                      <FormattedMessage id="pages.SearchMap.noResults.title" />
                    </Heading3>
                    <Lead />
                  </NoResults>
                )
              )}
            </Content>
          </LiftedContent>
        )}

        {positioned && (listView || !location) && (
          <BottomBar>
            <SearchViewToggle active="map" />

            <ResultsButton active={listView} onClick={() => changeListView(!listView)}>
              <Icon name={`${listView ? "chevrons-down" : "chevrons-up"}`} size="small" spaced="right" />
              {resultCount > 0 ? (
                <>
                  <FormattedMessage id="pages.SearchMap.showList" />
                  {` (${resultCount})`}
                </>
              ) : isLoading ? (
                <FormattedMessage id="pages.SearchMap.fetching" />
              ) : (
                <FormattedMessage id="pages.SearchMap.noResults.short" />
              )}
            </ResultsButton>
          </BottomBar>
        )}
      </SearchWrapper>
    </Wrapper>
  );
};

Search.propTypes = {
  visitedProviders: PropTypes.object,
  center: PropTypes.object,
  address: PropTypes.string,
  term: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  listView: PropTypes.bool.isRequired,
  match: PropTypes.object.isRequired,
  results: PropTypes.array,
  onlineResults: PropTypes.array,
  metadata: PropTypes.object,
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  setFilter: PropTypes.func.isRequired,

  /* Dispatchable actions*/
  changeListView: PropTypes.func.isRequired,
  clearAddress: PropTypes.func.isRequired,
  changeAddress: PropTypes.func.isRequired,
  searchMapResults: PropTypes.func.isRequired,
};

Search.defaultProps = {
  visitedProviders: null,
  center: null,
  address: null,
  results: [],
  onlineResults: [],
  metadata: {},
  term: null,
};

export default injectIntl(Search);
